import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useChatbot } from '@libertify.frontend/chatbot/index';
import type { ChatMessage } from '@libertify.frontend/chatbot/index';
import type { ChatbotMessage, ChatSource } from '@libertify/ui';
import { useAnalytics } from './useAnalytics';
import { toChatSourcePosition } from './toChatSourcePosition';
import {
  ChatQuerySource,
  MediaAnalyticsEvents,
  type MessageOrigin,
} from '../types';

/** The subset of the streamed socket payload this hook reads in onMessage. */
type ChatSocketResponse = { session_id?: string };

/**
 * Auth the chatbot socket presents in its `OpenSocket` payload. How these are
 * obtained is app-specific (cookie PIN/password + `?access_token=` for the
 * public viewer; the logged-in user's bearer for admin), so the consumer
 * supplies them via `resolveAuth`.
 */
export interface ChatAuth {
  bearerToken?: string;
  password?: string;
}

export interface UseExperienceChatbotArgs {
  /** WebSocket endpoint (per app: media-player `CHATBOT_URL`, admin `CHATBOT_WS_URL`). */
  chatbotUrl: string;
  /** Owning project id (`publication.project_id`). */
  projectId: string;
  /** Instance / publication id (`publication.instance_id`). */
  instanceId: string;
  /** Owning organization id, used as the chatbot's organization context. */
  organizationId?: string;
  /**
   * Document-scoped session fallback. When set (non-empty), `OpenSocket`
   * presents `document_ids` INSTEAD of `project_id`/`publication_id` — the
   * scope the legacy admin chat uses. For projects whose publication instance
   * doesn't exist (videoless projects 400 on the instances endpoint), this is
   * the only scope the chatbot backend can resolve. Publication scope stays
   * preferred when an instance exists: it's what the Chatbot add-on's
   * functional settings (greeting, tone, answer scope) apply to.
   */
  documentIds?: string[];
  /**
   * App-specific auth resolver, called when the socket opens. Lets each app
   * authenticate its own way (cookies + preview token, or a session bearer)
   * without this hook depending on any one app's auth plumbing.
   */
  resolveAuth: () => ChatAuth | Promise<ChatAuth>;
  /** Avatar applied to assistant bubbles (consumer-translated initials). */
  assistantAvatar?: NonNullable<ChatbotMessage['avatar']>;
  /**
   * Gate for the initial socket connection. The `OpenSocket` payload carries
   * the organization/publication context, so consumers whose context loads
   * asynchronously (admin fetches the publication client-side) MUST hold the
   * connection until it's available — connecting earlier sends
   * `organization_id: undefined`, the backend never establishes the session,
   * no welcome message (or suggestions) ever arrives, and there is no retry.
   * Defaults to true for consumers whose data is ready at mount (sales,
   * media-player: server-fetched).
   */
  ready?: boolean;
  /**
   * Don't start the handshake deadline until `ready` is true. For consumers
   * whose `ready` can legitimately stay false for minutes (admin
   * experience-v2 waits for the Chatbot add-on to generate). Defaults to false.
   */
  holdTimeoutUntilReady?: boolean;
}

export interface ExperienceChatbot {
  /** Flat user/assistant bubbles for `<ChatbotPanel messages>`. */
  messages: ChatbotMessage[];
  /** Send a query. `source` defaults to `'user'`; suggestion chips pass `'suggested'`. */
  onSubmit: (text: string, source?: ChatQuerySource) => void;
  /** Fired when the chat input gains focus. */
  onInputFocus: () => void;
  /** True while a response is streaming — drives the input's read-only state. */
  submitting: boolean;
  /** Latest assistant follow-up suggestions, rendered as prompt chips. */
  suggestions: string[];
  /**
   * True until the backend answers the handshake — the welcome message and its
   * initial suggested questions arrive in that same first frame, so there is
   * nothing worth showing before it lands.
   */
  loading: boolean;
  /** The handshake errored or timed out; nothing will arrive without a retry. */
  failed: boolean;
  /** Drop the dead socket and handshake again. Only meaningful while `failed`. */
  onRetry: () => void;
}

/**
 * How long to wait for the welcome frame before giving up. The backend can
 * accept the socket and then stay silent (rejected auth, unknown publication)
 * without ever emitting an error event, so a deadline is the only way out of
 * the loading state in that case.
 */
const HANDSHAKE_TIMEOUT_MS = 20_000;

/**
 * Map the backend's source origins onto the structural `ChatSource` the
 * reference cards render. `position` is left undefined when the origin has no
 * positional zone (the card then can't scroll to a precise highlight).
 */
const toChatSources = (origins: MessageOrigin[] | undefined): ChatSource[] =>
  (origins ?? []).map((origin, index) => {
    const position = origin.zone?.position;
    return {
      id: `${origin.document_id}-${position?.page_number ?? index}-${index}`,
      documentId: origin.document_id,
      quote: origin.zone?.quote ?? origin.text ?? '',
      pageNumber: position?.page_number,
      position: toChatSourcePosition(position),
    };
  });

/**
 * Drives the new `<ChatbotPanel>` with the existing WebSocket chatbot
 * (`@libertify.frontend/chatbot`) — the same data layer the legacy viewer
 * uses, a different UI. Shared across apps; each supplies its own `chatbotUrl`
 * and `resolveAuth` (the public viewer uses cookie creds + the preview token,
 * admin uses the logged-in user's bearer).
 *
 * The socket connects once on mount so the AI welcome message and its initial
 * follow-up questions arrive immediately. The session id is kept in-memory for
 * reconnects within the page.
 */
export function useExperienceChatbot({
  chatbotUrl,
  projectId,
  instanceId,
  organizationId,
  documentIds,
  resolveAuth,
  assistantAvatar,
  ready = true,
  holdTimeoutUntilReady = false,
}: UseExperienceChatbotArgs): ExperienceChatbot {
  const { trackEvent } = useAnalytics({
    projectId,
    publicationId: instanceId,
    organizationId,
  });

  const sessionIdRef = useRef<string | undefined>(undefined);
  // Holds a query submitted before the socket is ready; flushed once it is.
  const pendingQueryRef = useRef<string | null>(null);
  // Connect exactly once even though `connect`'s identity changes each render.
  const connectedRef = useRef(false);
  // The chatbot lib never clears `isBusy` on a socket error, so without this
  // the input would stay locked and the bubble stuck "typing" forever. Set on
  // error, cleared when the socket (re)opens.
  const [hasError, setHasError] = useState(false);
  // Set when the welcome frame never showed up (see HANDSHAKE_TIMEOUT_MS).
  const [timedOut, setTimedOut] = useState(false);
  // True between onRetry() and the retried socket's onOpen: our own reconnect
  // tears the old socket down first, and that expected close must not be read
  // as a backend rejection.
  const retryingRef = useRef(false);

  const {
    isReady,
    sendQuery,
    messageHistory,
    isBusy,
    connect,
    reconnect,
    sendJsonMessage,
  } = useChatbot<ChatSocketResponse>(chatbotUrl, {
    historyKey: `${projectId}:${instanceId}`,
    onOpen: async () => {
      setHasError(false);
      retryingRef.current = false;
      const { bearerToken, password } = await resolveAuth();
      sendJsonMessage({
        action: 'OpenSocket',
        context: {
          organization_id: 'libertify',
          session_id: sessionIdRef.current,
          organization_context: {
            organization_id: organizationId,
            // Same either/or the legacy viewer sends: document scope when the
            // consumer has no resolvable publication (see documentIds docs),
            // publication scope otherwise.
            ...(documentIds?.length
              ? { document_ids: documentIds }
              : { project_id: projectId, publication_id: instanceId }),
            bearer_token: bearerToken,
            password,
          },
        },
      });
    },
    onMessage: (data: ChatSocketResponse) => {
      if (data?.session_id) sessionIdRef.current = data.session_id;
    },
    onError: () => setHasError(true),
    onClose: () => {
      // The backend rejects a bad OpenSocket by closing the socket (clean
      // code 1000, no error frame), so a pre-handshake close IS the
      // rejection — surface it immediately instead of waiting out the
      // deadline. Post-handshake closes (idle drops) stay silent, and the
      // teardown close of our own retry cycle is skipped via the ref.
      if (retryingRef.current) return;
      if (!isReady && messageHistory.length === 0) setHasError(true);
    },
  });

  // Connect once as soon as the consumer's context is ready so the AI welcome
  // message + its initial follow-up questions arrive immediately. `connect` is
  // a fresh function each render, so the ref guard keeps this from re-firing
  // on every render. Waiting for `ready` matters: connecting before the
  // publication context exists sends an incomplete OpenSocket the backend
  // never answers (see UseExperienceChatbotArgs.ready).
  useEffect(() => {
    if (!ready || connectedRef.current) return;
    connectedRef.current = true;
    connect();
  }, [ready, connect]);

  // Flush a query that was submitted before the socket finished connecting.
  useEffect(() => {
    if (isReady && pendingQueryRef.current) {
      sendQuery(pendingQueryRef.current);
      pendingQueryRef.current = null;
    }
  }, [isReady, sendQuery]);

  // The handshake is settled once the socket has answered — or, when the chat
  // is reopened within the same page session, as soon as the cached history is
  // restored (the welcome message is already in it).
  const handshakeDone = isReady || messageHistory.length > 0;
  const failed = !handshakeDone && (hasError || timedOut);

  // Give up on a handshake that never completes. Deliberately NOT gated on
  // `ready`: a consumer whose context never resolves (admin's publication
  // fetch is silent on error) would otherwise leave the panel loading forever
  // with no way out, since `loading` isn't gated on `ready` either.
  // `timedOut` must be in the guard and deps: retry clears it, and that
  // clearing is what re-arms the deadline. Without it the effect never re-runs
  // on retry, so a retry that also goes unanswered would wait forever.
  // Opt-in: the deadline starts once the socket is allowed to connect.
  const timeoutHeld = holdTimeoutUntilReady && !ready;
  useEffect(() => {
    if (handshakeDone || hasError || timedOut || timeoutHeld) return;
    const timer = globalThis.setTimeout(
      () => setTimedOut(true),
      HANDSHAKE_TIMEOUT_MS,
    );
    return () => globalThis.clearTimeout(timer);
  }, [handshakeDone, hasError, timedOut, timeoutHeld]);

  const onRetry = useCallback(() => {
    setHasError(false);
    setTimedOut(false);
    // Only cycle the socket when there's a context to hand the backend —
    // reconnecting without one sends an incomplete OpenSocket that is never
    // answered (see UseExperienceChatbotArgs.ready). Clearing `timedOut` alone
    // re-arms the deadline, giving the consumer's context another window.
    if (ready) {
      retryingRef.current = true;
      reconnect();
    }
  }, [ready, reconnect]);

  const onSubmit = useCallback(
    (text: string, source: ChatQuerySource = 'user') => {
      trackEvent(MediaAnalyticsEvents.ChatMessageSend, {
        input_position: 'chat',
        query_source: source,
        chat_session_id: sessionIdRef.current,
      });
      if (source === 'suggested') {
        trackEvent(MediaAnalyticsEvents.SuggestedQuerySelected, {
          query: text,
          chat_session_id: sessionIdRef.current,
        });
      }
      if (isReady) {
        sendQuery(text);
      } else {
        // Park the query; it flushes once the socket is ready. Only trigger a
        // connect when the context is ready — connecting earlier would send
        // an incomplete OpenSocket (see UseExperienceChatbotArgs.ready).
        pendingQueryRef.current = text;
        if (ready) connect();
      }
    },
    [isReady, sendQuery, connect, trackEvent, ready],
  );

  const onInputFocus = useCallback(() => {
    trackEvent(MediaAnalyticsEvents.ChatInputFocus, {
      input_position: 'chat',
      chat_session_id: sessionIdRef.current,
    });
  }, [trackEvent]);

  // Follow-up suggestions ride on the latest record's metadata — including the
  // welcome message's initial questions. Hidden while a response streams so a
  // new query clears the chips until the next answer settles.
  const suggestions = useMemo<string[]>(() => {
    if (isBusy) return [];
    const last = messageHistory[messageHistory.length - 1] as
      | ChatMessage
      | undefined;
    return last?.metadata?.suggested_questions ?? [];
  }, [messageHistory, isBusy]);

  // Map the chatbot's per-query records into flat user/assistant bubbles. The
  // assistant text can arrive in `message` (streamed chunks) OR in
  // `messages[0].content` (the welcome message + some answers) — mirror the
  // legacy renderer and read both.
  const messages = useMemo<ChatbotMessage[]>(() => {
    const out: ChatbotMessage[] = [];
    (messageHistory as ChatMessage[]).forEach((m, i) => {
      const key = m.queryId || m.query_id || `m${i}`;
      if (m.query) {
        out.push({ id: `${key}-u`, role: 'user', body: m.query });
      }
      // Sources ride on `metadata.sources` (same payload the legacy viewer
      // reads); the chatbot lib's ChatMessage type omits it, so read through a
      // local cast to the richer experience-viewer shape.
      const record = m as unknown as {
        metadata?: { sources?: MessageOrigin[] } | null;
        is_final?: boolean;
      };
      const sources = toChatSources(record.metadata?.sources);
      // Mirrors the legacy renderer: streaming (is_final === false) with no
      // metadata yet means sources are still being resolved.
      const processingSources = record.is_final === false && !record.metadata;
      const body = m.message || m.messages?.[0]?.content || '';
      if (body) {
        out.push({
          id: `${key}-a`,
          role: 'assistant',
          body,
          avatar: assistantAvatar,
          sources,
          processingSources,
          // A socket error ends the stream, so the bubble isn't stuck aria-busy.
          streaming: record.is_final === false && !hasError,
        });
      } else if (m.query && !hasError) {
        // Awaiting the response — show the typing indicator. On error we drop
        // the empty bubble so it isn't stuck "typing" forever.
        out.push({
          id: `${key}-a`,
          role: 'assistant',
          body: '',
          pending: true,
          // The pending bubble becomes the answer in place (same key), so it
          // must already be flagged to animate from the first chunk.
          streaming: true,
          avatar: assistantAvatar,
        });
      }
    });
    return out;
  }, [messageHistory, assistantAvatar, hasError]);

  return {
    messages,
    onSubmit,
    onInputFocus,
    submitting: isBusy && !hasError,
    suggestions,
    loading: !handshakeDone && !failed,
    failed,
    onRetry,
  };
}
